(() => {
  const $$ = (root, sel) => Array.from(root.querySelectorAll(sel));
  const $ = (root, sel) => root.querySelector(sel);

  function safeJson(text, fallback) {
    try { return JSON.parse(text); } catch { return fallback; }
  }

  function moneyFormatter(currency) {
    const lang = document.documentElement.lang || "en-US";
    try { return new Intl.NumberFormat(lang, { style: "currency", currency }); }
    catch { return new Intl.NumberFormat(lang, { style: "currency", currency: "USD" }); }
  }

  // For price-per-item only: show cents normally, keep a third decimal only when needed.
  function moneyFormatterPricePerUnit(currency) {
    const lang = document.documentElement.lang || "en-US";
    try {
      return new Intl.NumberFormat(lang, {
        style: "currency",
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 3
      });
    } catch {
      return new Intl.NumberFormat(lang, {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 3
      });
    }
  }

  function fmtMoney(fmt, cents) {
    const n = Number(cents || 0) / 100;
    return fmt.format(n);
  }

  function fmtMoneyDollars(fmt, dollars) {
    return fmt.format(Number(dollars || 0));
  }

  function toOrdinal(day) {
    const n = Number(day || 0);
    const v = n % 100;
    if (v >= 11 && v <= 13) return `${n}th`;
    switch (n % 10) {
      case 1: return `${n}st`;
      case 2: return `${n}nd`;
      case 3: return `${n}rd`;
      default: return `${n}th`;
    }
  }

  function addCalendarDays(date, days) {
    const next = new Date(date);
    next.setDate(next.getDate() + days);
    return next;
  }

  function formatDeliveryDate(date) {
    const month = date.toLocaleString(undefined, { month: "long" });
    return `${month} ${toOrdinal(date.getDate())}`;
  }

  function formatDeliveryDateRange(now = new Date()) {
    return formatDeliveryDate(addCalendarDays(now, 5));
  }

  // Robust delegated gallery thumb -> main swap
  function initOfferGallery(root) {
    const gallery = root.querySelector("[data-offer-gallery], .offer-gallery");
    if (!gallery || gallery.__offerGalleryInit) return;
    gallery.__offerGalleryInit = true;

    function getThumbAlt(thumb) {
      const img = thumb?.querySelector("img");
      const alt = (img?.getAttribute("alt") || "").trim();
      if (alt) return alt;
      const aria = (thumb?.getAttribute("aria-label") || "").trim();
      return aria || "Product image";
    }

    function getMainImg() {
      const el =
        gallery.querySelector("[data-offer-main]") ||
        gallery.querySelector(".offer-gallery__main") ||
        gallery.querySelector(".offer-gallery__stage img");
      if (!el) return null;
      if (el.tagName === "PICTURE") return el.querySelector("img");
      if (el.tagName === "IMG") return el;
      return el.querySelector("img");
    }

    function setPictureSources(newSrc) {
      const pic = gallery.querySelector("[data-offer-main] picture, .offer-gallery__stage picture");
      if (!pic) return;
      pic.querySelectorAll("source").forEach((s) => { s.srcset = newSrc; });
    }

    gallery.addEventListener("click", (e) => {
      const thumb = e.target.closest("[data-offer-thumb], .offer-gallery__thumb");
      if (!thumb || !gallery.contains(thumb)) return;

      const newSrc =
        thumb.getAttribute("data-offer-src") ||
        thumb.getAttribute("data-full") ||
        thumb.getAttribute("data-src") ||
        thumb.getAttribute("href");

      if (!newSrc) return;

      e.preventDefault();

      const img = getMainImg();
      if (img) {
        img.src = newSrc;
        img.srcset = newSrc;
        img.removeAttribute("sizes");
        img.loading = "eager";
        const alt = getThumbAlt(thumb);
        if (alt) img.setAttribute("alt", alt);
      }
      setPictureSources(newSrc);

      gallery.querySelectorAll("[data-offer-thumb], .offer-gallery__thumb").forEach((t) => {
        t.classList.remove("is-active");
        t.setAttribute("aria-pressed", "false");
      });
      thumb.classList.add("is-active");
      thumb.setAttribute("aria-pressed", "true");
    });

    gallery.querySelectorAll("[data-offer-thumb], .offer-gallery__thumb").forEach((thumb) => {
      const img = thumb.querySelector("img");
      if (!img) return;
      const currentAlt = (img.getAttribute("alt") || "").trim();
      if (!currentAlt) img.setAttribute("alt", getThumbAlt(thumb));
    });
    const main = getMainImg();
    if (main && !(main.getAttribute("alt") || "").trim()) {
      const activeThumb =
        gallery.querySelector("[data-offer-thumb].is-active, .offer-gallery__thumb.is-active") ||
        gallery.querySelector("[data-offer-thumb], .offer-gallery__thumb");
      main.setAttribute("alt", getThumbAlt(activeThumb));
    }
  }

  function clamp(n, min, max) { return Math.max(min, Math.min(max, n)); }

  /**
   * Shrink text until it fits in its own box (no ellipsis).
   * Uses binary search on font-size.
   */
  function fitTextToBox(el, minPx, maxPx) {
    if (!el || !el.isConnected) return;
    const boxW = el.clientWidth;
    if (!boxW || boxW < 10) return;

    el.style.fontSize = `${maxPx}px`;
    if (el.scrollWidth <= boxW) return;

    let lo = minPx;
    let hi = maxPx;

    for (let i = 0; i < 10; i++) {
      const mid = (lo + hi) / 2;
      el.style.fontSize = `${mid}px`;
      if (el.scrollWidth > boxW) hi = mid;
      else lo = mid;
    }

    el.style.fontSize = `${lo}px`;
  }

  function parsePctFromText(str) {
    const m = String(str || "").match(/(\d+(?:\.\d+)?)\s*%/);
    if (!m) return null;
    return clamp(Math.round(parseFloat(m[1])), 0, 95);
  }

  function planPctFromAlloc(alloc, fallbackCompareAtCents) {
    if (!alloc) return null;

    const p = Number(alloc.price);
    const cRaw = Number(alloc.compareAt);

    if (!isFinite(p) || p <= 0) return null;

    const c = (isFinite(cRaw) && cRaw > 0) ? cRaw : Number(fallbackCompareAtCents);
    if (!isFinite(c) || c <= 0) return null;

    return clamp(Math.round((1 - (p / c)) * 100), 0, 95);
  }

  function allocKey(variantId, planId) {
    return `${String(variantId)}:${String(planId)}`;
  }

  function replacePctInTemplate(template, pct) {
    const t = String(template || "").trim();
    if (!t) return `Save ${pct}%`;

    // If the template already contains a percent, replace just the number
    if (/%/.test(t) && /(\d+(?:\.\d+)?)\s*%/.test(t)) {
      return t.replace(/(\d+(?:\.\d+)?)\s*%/, `${pct}%`);
    }

    // Fallbacks
    if (/save/i.test(t)) return `Save ${pct}%`;
    return `${pct}% Off`;
  }

  function initOfferCTAv2(root) {
    initOfferGallery(root);

    const configEl = $(root, "[data-offer-v2-config]");
    if (!configEl || root.__offerCTAv2Init) return;

    root.__offerCTAv2Init = true;

    const config = safeJson(configEl.textContent, null);
    if (!config) return;

    const fmt = moneyFormatter(config.currency);
    const fmt3 = moneyFormatterPricePerUnit(config.currency);

    const variants = Array.isArray(config.variants) ? config.variants : [];
    const variantsById = new Map(variants.map(v => [String(v.id), v]));
    const itemsPerBag = Math.max(1, Number(config.itemsPerBag || config.treatsPerBag || 1));
    const showItemsPerBag = config.showItemsPerBag !== false && Number(config.itemsPerBag || config.treatsPerBag || 0) > 0;
    const itemUnitSingular = String(config.itemUnitSingular || "item").trim() || "item";
    const useProductFlavorMode = config.useProductFlavorMode === true;
    let spAllocById = new Map(
      variants.map(v => [
        String(v.id),
        (v && v.spPrice != null)
          ? { price: Number(v.spPrice), compareAt: Number(v.spCompareAt ?? v.price) }
          : null
      ])
    );
    let spAllocByVariantPlan = new Map();
    variants.forEach((v) => {
      const planId = Number(v?.spId || 0);
      if (!planId || v?.spPrice == null) return;
      spAllocByVariantPlan.set(allocKey(v.id, planId), {
        price: Number(v.spPrice),
        compareAt: Number(v.spCompareAt ?? v.price)
      });
    });

    const defaults = config.defaults || {};
    const defaultQty = clamp(Number(defaults.qty || 3), 1, 3);
    const defaultPurchaseOption = (String(defaults.purchaseOption || "subscribe") === "one_time") ? "one_time" : "subscribe";
    const defaultVariantId = String(defaults.variantId || "").trim();

    const discounts = config.discounts || {};
    const sellingPlansByHandle = (config.sellingPlansByHandle && typeof config.sellingPlansByHandle === "object")
      ? config.sellingPlansByHandle
      : {};
    const oneTimePct = 0; // kept only as fallback if a qty badge has no %
    const subPct = 0;

    const sellingPlanIdRaw = (discounts.subscribeSellingPlanId || "").toString().trim();
    const sellingPlanId = (sellingPlanIdRaw && /^\d+$/.test(sellingPlanIdRaw)) ? Number(sellingPlanIdRaw) : null;

    const perkIcons = config.perkIcons || {};
    const includedIconUrl = (perkIcons.included || "").toString().trim();
    const excludedIconUrl = (perkIcons.excluded || "").toString().trim();

    const form = $(root, "[data-offer-v2-form]");
    const atcBtn = $(root, "[data-offer-v2-atc]");
    const promoBtn = $(root, "[data-offer-v2-promo-atc]");
    const revliftLinesEl = form ? form.querySelector("[data-offer-v2-revlift-lines]") : null;
    const primaryVariantInput = form ? form.querySelector("[data-offer-v2-primary-variant]") : null;
    const primaryQuantityInput = form ? form.querySelector("[data-offer-v2-primary-quantity]") : null;
    const qtyCards = $$(root, "[data-offer-v2-qty]");
    const localQtyDiscounts = new Map(qtyCards.map((card) => {
      const qty = Number(card.getAttribute("data-offer-v2-qty") || 0);
      const pct = parsePctFromText(card.querySelector(".offer-cta-v2__qtyBadge")?.textContent || "");
      return [qty, pct == null ? 0 : pct];
    }));
    const qtyBadgeTemplates = new Map(qtyCards.map((card) => [
      Number(card.getAttribute("data-offer-v2-qty") || 0),
      card.querySelector(".offer-cta-v2__qtyBadge")?.textContent?.trim() || ""
    ]));
    const qtyCardsWrap = $(root, ".offer-cta-v2__qtyCards");
    const deliveryEl = $(root, "[data-offer-v2-delivery]");
    const deliveryDateEl = $(root, "[data-offer-v2-delivery-date]");

    const oneBadgeEl = $(root, '[data-offer-v2-po-badge="one_time"]');
    const subBadgeEl = $(root, '[data-offer-v2-po-badge="subscribe"]');

    const oneBadgeTemplate = oneBadgeEl ? oneBadgeEl.textContent.trim() : "";
    const subBadgeTemplate = subBadgeEl ? subBadgeEl.textContent.trim() : "";

    const stack = $(root, "[data-offer-v2-flavor-stack]");
    const tpl = $(root, "template[data-offer-v2-flavor-template]");
    const helpEl = $(root, "[data-offer-v2-help]");

    const poCards = $$(root, "[data-offer-v2-po]");
    const poRadios = $$(root, ".offer-cta-v2__poRadio");

    let bagQty = 1;
    let purchaseOption = "one_time";

    function placeDeliveryBeforeAtc() {
      if (!deliveryEl || !atcBtn || !atcBtn.parentNode) return;
      if (deliveryEl.nextElementSibling === atcBtn) return;

      atcBtn.parentNode.insertBefore(deliveryEl, atcBtn);
    }

    function qtySavePctFor(q) {
      return localQtyDiscounts.get(Number(q)) ?? oneTimePct;
    }

    function effectiveQtySavePctFor(q, purchaseOptionMode = "one_time") {
      const localPct = qtySavePctFor(q);
      return window.RevLiftPricing?.bestDiscountPercent
        ? window.RevLiftPricing.bestDiscountPercent(localPct, {
            purchaseOptionMode,
            lines: selectedIds().map(id => ({ id, quantity: 1,
              price: Number(variantsById.get(String(id))?.price || 0),
              productHandle: variantsById.get(String(id))?.handle || config.productHandle })),
            quantity: Number(q) || 1
          })
        : localPct;
    }

    function renderQtyCardBadges() {
      qtyCards.forEach((card) => {
        const qty = Number(card.getAttribute("data-offer-v2-qty") || 0);
        const pct = effectiveQtySavePctFor(qty);
        let badge = card.querySelector(".offer-cta-v2__qtyBadge");
        if (!badge && pct > 0) {
          badge = document.createElement("span");
          badge.className = "offer-cta-v2__qtyBadge";
          card.prepend(badge);
        }
        if (!badge) return;
        badge.hidden = pct <= 0;
        if (pct > 0) badge.textContent = replacePctInTemplate(qtyBadgeTemplates.get(qty), pct);
      });
    }

    async function hydrateSellingPlanAllocationsIfMissing() {
      const handlesToLoad = [];
      if (useProductFlavorMode) {
        const seen = new Set();
        variants.forEach((v) => {
          const h = String(v?.handle || "").trim();
          if (!h || seen.has(h)) return;
          seen.add(h);
          handlesToLoad.push(h);
        });
      } else {
        const handle = (config.productHandle || "").toString().trim();
        if (handle) handlesToLoad.push(handle);
      }
      if (!handlesToLoad.length) return;

      try {
        for (const handle of handlesToLoad) {
          const res = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
            headers: { "Accept": "application/json" }
          });
          if (!res.ok) continue;

          const prod = await res.json();

          (prod.variants || []).forEach((pv) => {
            const vid = String(pv.id);
            const allocs = Array.isArray(pv.selling_plan_allocations) ? pv.selling_plan_allocations : [];
            allocs.forEach((a) => {
              const planId = Number(a?.selling_plan_id || 0);
              const price = Number(a?.price);
              const compareAt = Number(
                a?.compare_at_price != null ? a.compare_at_price : (pv.price != null ? pv.price : 0)
              );
              if (!planId || !isFinite(price) || price <= 0) return;
              spAllocByVariantPlan.set(allocKey(vid, planId), {
                price,
                compareAt: (isFinite(compareAt) && compareAt > 0) ? compareAt : Number(pv.price || 0)
              });
            });
          });
        }

        rerenderAll();
      } catch (e) {
        console.warn("Failed to hydrate selling plan allocations", e);
      }
    }

    function perkIncluded(perk, q) {
      const key = `q${q}`;
      return !!(config?.perks?.[perk]?.[key]);
    }

    function setMarkIcon(markEl, included) {
      if (!markEl) return;
      const url = included ? includedIconUrl : excludedIconUrl;

      if (url) {
        markEl.classList.add("has-img");
        const existing = markEl.querySelector("img");
        if (!existing || existing.getAttribute("src") !== url) {
          markEl.innerHTML = `<img src="${url}" alt="" loading="lazy" />`;
        }
      } else {
        markEl.classList.remove("has-img");
        markEl.innerHTML = "";
      }
    }

    function renderQtyCardPerks() {
      const perkEls = $$(root, "[data-offer-v2-perk]");
      perkEls.forEach((el) => {
        const perk = el.getAttribute("data-offer-v2-perk");
        const q = Number(el.getAttribute("data-offer-v2-perk-q") || 1);
        const included = perkIncluded(perk, q);

        el.classList.toggle("is-included", included);
        el.classList.toggle("is-excluded", !included);

        setMarkIcon(el.querySelector(".offer-cta-v2__qtyMark"), included);
      });
    }

    function fitQtyPerkText() {
      const texts = $$(root, ".offer-cta-v2__qtyPerkText:not([data-no-fit])");
      const styles = getComputedStyle(root);
      const maxPx = parseFloat(styles.getPropertyValue("--perk-font-max")) || 12;
      const minPx = parseFloat(styles.getPropertyValue("--perk-font-min")) || 9;

      texts.forEach((t) => { t.style.fontSize = ""; });

      requestAnimationFrame(() => {
        texts.forEach((t) => fitTextToBox(t, minPx, maxPx));
      });
    }

    function ensureRows(n) {
      if (!stack || !tpl) return;

      $$(stack, "[data-offer-v2-bag-row]").forEach(r => r.remove());

      const firstAvailable = variants.find(v => v && v.available);
      const fallbackId = String((firstAvailable || variants[0] || {}).id || "");

      for (let i = 0; i < n; i++) {
        const frag = tpl.content.cloneNode(true);
        const boxes = Array.from(frag.querySelectorAll("[data-offer-v2-pill]"));

        const label = frag.querySelector("[data-offer-v2-bag-label]");
        if (label) label.textContent = `Bag ${i + 1}`;

        // Choose default variant ID if provided AND enabled, else first enabled, else fallback
        let chosen = fallbackId;

        if (defaultVariantId) {
          const match = boxes.find(b => !b.disabled && String(b.getAttribute("data-offer-v2-variant-id") || "") === defaultVariantId);
          if (match) chosen = defaultVariantId;
        }

        const firstEnabled = boxes.find(b => !b.disabled);
        if (!chosen && firstEnabled) chosen = String(firstEnabled.getAttribute("data-offer-v2-variant-id") || "");

        boxes.forEach((b) => {
          const id = String(b.getAttribute("data-offer-v2-variant-id") || "");
          const active = id && id === chosen;

          b.classList.toggle("is-active", active);
          b.setAttribute("aria-pressed", active ? "true" : "false");

          b.addEventListener("click", (e) => {
            e.preventDefault();
            if (b.disabled) return;

            boxes.forEach((x) => {
              x.classList.remove("is-active");
              x.setAttribute("aria-pressed", "false");
            });

            b.classList.add("is-active");
            b.setAttribute("aria-pressed", "true");

            rerenderAll();
          });
        });

        stack.appendChild(frag);
      }
    }

    function selectedIds() {
      return $$(root, "[data-offer-v2-bag-row]").map((row) => {
        const active = row.querySelector("[data-offer-v2-pill].is-active");
        return String(active?.getAttribute("data-offer-v2-variant-id") || "");
      }).filter(Boolean);
    }

    function syncRevliftFormLines() {
      if (!form || !revliftLinesEl) return;
      const ids = selectedIds();
      // Publish the same merchandise-checked rates as the quantity cards. The
      // selected variants can belong to a different product than the PDP URL.
      form.setAttribute("data-revlift-line-pricing", "");
      form.setAttribute("data-revlift-one-time-discount-percent", String(effectiveQtySavePctFor(ids.length, "one_time")));
      form.setAttribute("data-revlift-subscription-discount-percent", String(effectiveQtySavePctFor(ids.length, "subscription")));
      revliftLinesEl.innerHTML = "";

      const counts = new Map();
      ids.forEach((id) => counts.set(String(id), (counts.get(String(id)) || 0) + 1));
      Array.from(counts.entries()).forEach(([id, qty], index) => {
        const idInput = document.createElement("input");
        idInput.type = "hidden";
        idInput.name = `items[${index}][id]`;
        idInput.value = id;
        revliftLinesEl.appendChild(idInput);

        const qtyInput = document.createElement("input");
        qtyInput.type = "hidden";
        qtyInput.name = `items[${index}][quantity]`;
        qtyInput.value = String(qty);
        revliftLinesEl.appendChild(qtyInput);
      });


      if (primaryQuantityInput) {
        primaryQuantityInput.value = String(Math.max(1, ids.length));
        primaryQuantityInput.setAttribute("value", primaryQuantityInput.value);
      }

      if (primaryVariantInput && ids[0]) {
        const nextId = String(ids[0]);
        primaryVariantInput.value = nextId;
        primaryVariantInput.setAttribute("value", nextId);
        primaryVariantInput.dispatchEvent(new Event("input", { bubbles: true }));
        primaryVariantInput.dispatchEvent(new Event("change", { bubbles: true }));
      }

      const refresh = () => {
        form.dispatchEvent(new Event("revlift:refresh", { bubbles: true }));
        document.dispatchEvent(new Event("revlift:refresh"));
      };
      refresh();
      window.setTimeout(refresh, 0);
    }

    function syncPurchaseOptionFromRevlift() {
      if (!form) return;
      const checked = form.querySelector(
        'input[name="revlift_selling_plan_choice"]:checked, input[name="revlift_landing_selling_plan_choice"]:checked'
      );
      if (checked) {
        purchaseOption = checked.value === "subscription" ? "subscribe" : "one_time";
        return;
      }
      const singleSellingPlan = form.querySelector('input[name="selling_plan"]');
      if (singleSellingPlan && String(singleSellingPlan.value || "").trim()) {
        purchaseOption = "subscribe";
      }
    }

    function clearRevliftSellingPlanInputsForOneTime() {
      if (!form) return;
      form
        .querySelectorAll('input[name="selling_plan"], input[name^="items["][name$="[selling_plan]"]')
        .forEach((input) => input.remove());
    }

    function revliftSellingPlanByVariantId() {
      const plans = new Map();
      if (!form) return plans;
      let data = null;
      try { data = new FormData(form); } catch { return plans; }
      const idsByIndex = new Map();
      for (const [name, value] of data.entries()) {
        const idMatch = String(name).match(/^items\[(\d+)\]\[id\]$/);
        if (idMatch) idsByIndex.set(idMatch[1], String(value || ""));
      }
      for (const [name, value] of data.entries()) {
        const planMatch = String(name).match(/^items\[(\d+)\]\[selling_plan\]$/);
        if (!planMatch || !value) continue;
        const id = idsByIndex.get(planMatch[1]);
        if (id) plans.set(id, Number(value));
      }
      const singlePlan = Number(form.querySelector('input[name="selling_plan"]')?.value || 0);
      if (singlePlan > 0) {
        idsByIndex.forEach((id) => plans.set(String(id), singlePlan));
      }
      return plans;
    }

    function calculationIds() {
      if (!useProductFlavorMode && defaultVariantId && variantsById.has(defaultVariantId)) {
        return Array.from({ length: Math.max(1, bagQty) }, () => defaultVariantId);
      }

      return selectedIds();
    }

    function anySoldOut(ids) {
      return ids.some((id) => {
        const v = variantsById.get(String(id));
        return v ? !v.available : true;
      });
    }

    function totalCents(ids) {
      let t = 0;
      ids.forEach((id) => {
        const v = variantsById.get(String(id));
        if (v) t += Number(v.price || 0);
      });
      return t;
    }

    function discounted(total, pct) {
      return total - Math.floor(total * pct / 100 + 1e-7);
    }

    function setPurchaseOption(val) {
      purchaseOption = (val === "subscribe") ? "subscribe" : "one_time";

      poCards.forEach((card) => {
        const isActive = card.getAttribute("data-offer-v2-po") === purchaseOption;
        card.classList.toggle("is-active", isActive);
      });

      poRadios.forEach((r) => { r.checked = (r.value === purchaseOption); });

      rerenderAll();
    }

    // Everything App owns widget totals and unit labels; these prices are fallback-only.
    function renderPurchaseOptions() {
      const ids = calculationIds();
      const base = totalCents(ids);

      const qtyPct = effectiveQtySavePctFor(bagQty);
      const subscriptionQtyPct = effectiveQtySavePctFor(bagQty, "subscription");
      const onePrice = ids.reduce((sum, id) =>
        sum + discounted(Number(variantsById.get(String(id))?.price || 0), qtyPct), 0);

      const subPrice = ids.reduce((sum, id) => {
        const v = variantsById.get(String(id));
        const unit = Number(v?.price || 0);
        const variantHandle = String(v?.handle || "").trim();
        const handlePlanId = Number((variantHandle && sellingPlansByHandle[variantHandle]) || 0);
        const variantPlanId = Number(v?.spId || 0);
        const selectedPlanId = useProductFlavorMode
          ? (handlePlanId || variantPlanId)
          : (variantPlanId || sellingPlanId || handlePlanId);
        const alloc = (selectedPlanId ? spAllocByVariantPlan.get(allocKey(id, selectedPlanId)) : null)
          || spAllocById.get(String(id));
        const planPct = planPctFromAlloc(alloc, unit);

        const planPrice = alloc?.price != null ? Number(alloc.price)
          : (planPct != null ? Math.round(unit * (100 - planPct) / 100) : unit);
        const unitFinal = discounted(planPrice, subscriptionQtyPct);
        return sum + unitFinal;
      }, 0);

      const onePriceEl = $(root, '[data-offer-v2-po-price="one_time"]');
      const oneCompareEl = $(root, '[data-offer-v2-po-compare="one_time"]');
      const subPriceEl = $(root, '[data-offer-v2-po-price="subscribe"]');
      const subCompareEl = $(root, '[data-offer-v2-po-compare="subscribe"]');

      if (onePriceEl) onePriceEl.textContent = fmtMoney(fmt, onePrice);
      if (oneCompareEl) {
        oneCompareEl.textContent = fmtMoney(fmt, base);
        oneCompareEl.hidden = !(base > onePrice);
      }

      if (subPriceEl) subPriceEl.textContent = fmtMoney(fmt, subPrice);
      if (subCompareEl) {
        subCompareEl.textContent = fmtMoney(fmt, base);
        subCompareEl.hidden = !(base > subPrice);
      }

      if (oneBadgeEl) oneBadgeEl.textContent = replacePctInTemplate(oneBadgeTemplate, qtyPct);

      let combinedPct = 0;
      if (base > 0) combinedPct = clamp(Math.round((1 - subPrice / base) * 100), 0, 95);
      if (subBadgeEl) {
        subBadgeEl.textContent = subBadgeTemplate || replacePctInTemplate(subBadgeTemplate, combinedPct);
      }

      const onePTEl = $(root, '[data-offer-v2-po-per-item="one_time"], [data-offer-v2-po-per-treat="one_time"]');
      const subPTEl = $(root, '[data-offer-v2-po-per-item="subscribe"], [data-offer-v2-po-per-treat="subscribe"]');
      const onePBEl = $(root, '[data-offer-v2-po-per-bag="one_time"]');
      const subPBEl = $(root, '[data-offer-v2-po-per-bag="subscribe"]');

      const bags = Math.max(1, bagQty);
      const onePerBag = onePrice / bags;
      const subPerBag = subPrice / bags;
      if (onePBEl) {
        onePBEl.textContent = `${fmtMoney(fmt, onePerBag)}/bag`;
        onePBEl.hidden = showItemsPerBag;
      }
      if (subPBEl) {
        subPBEl.textContent = `${fmtMoney(fmt, subPerBag)}/bag`;
        subPBEl.hidden = showItemsPerBag;
      }

      if (!showItemsPerBag) {
        if (onePTEl) onePTEl.hidden = true;
        if (subPTEl) subPTEl.hidden = true;
        return;
      }

      const denom = Math.max(1, itemsPerBag * Math.max(1, bagQty));
      const onePTDollars = (onePrice / 100) / denom;
      const subPTDollars = (subPrice / 100) / denom;
      const oneUnitText = `~${fmtMoneyDollars(fmt3, onePTDollars)}/${itemUnitSingular}`;
      const subUnitText = `~${fmtMoneyDollars(fmt3, subPTDollars)}/${itemUnitSingular}`;

      if (onePTEl) {
        onePTEl.hidden = false;
        onePTEl.textContent = oneUnitText;
      }
      if (subPTEl) {
        subPTEl.hidden = false;
        subPTEl.textContent = subUnitText;
      }

    }

    function renderSoldOut() {
      const ids = selectedIds();
      const bad = ids.length === 0 || anySoldOut(ids);
      if (helpEl) helpEl.hidden = !bad;
      if (atcBtn) atcBtn.disabled = bad;
    }

    function renderDeliveryTimeline() {
      if (!deliveryEl || !deliveryDateEl) return;
      placeDeliveryBeforeAtc();
      const ids = selectedIds();
      const inStock = ids.length > 0 && !anySoldOut(ids);
      deliveryEl.hidden = !inStock;
      if (!inStock) return;
      deliveryDateEl.textContent = formatDeliveryDateRange();
    }

    function rerenderAll() {
      renderQtyCardBadges();
      renderPurchaseOptions();
      renderSoldOut();
      renderDeliveryTimeline();
      fitQtyPerkText();
      syncRevliftFormLines();
    }

    document.addEventListener("revlift:storewide-state", rerenderAll);

    if (window.MutationObserver && form && deliveryEl && atcBtn) {
      const deliveryPlacementObserver = new MutationObserver(placeDeliveryBeforeAtc);
      deliveryPlacementObserver.observe(form, { childList: true, subtree: true });
      window.setTimeout(placeDeliveryBeforeAtc, 0);
      window.setTimeout(placeDeliveryBeforeAtc, 250);
      window.setTimeout(placeDeliveryBeforeAtc, 1000);
    }

    function setQty(q) {
      bagQty = clamp(Number(q || 1), 1, 3);

      qtyCards.forEach((btn) => {
        const active = Number(btn.getAttribute("data-offer-v2-qty") || 0) === bagQty;
        btn.classList.toggle("is-active", active);
        btn.setAttribute("aria-pressed", active ? "true" : "false");
      });

      ensureRows(bagQty);
      rerenderAll();

      try {
        window.dispatchEvent(new CustomEvent("offer:quantityChange", {
          detail: { quantity: bagQty, purchaseOption: purchaseOption }
        }));
      } catch (e) {
        /* ignore */
      }
    }

    qtyCards.forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        setQty(btn.getAttribute("data-offer-v2-qty"));
      });
    });

    poRadios.forEach((r) => r.addEventListener("change", () => setPurchaseOption(r.value)));
    poCards.forEach((card) => card.addEventListener("click", () => {
      setPurchaseOption(card.getAttribute("data-offer-v2-po"));
    }));

    async function addItems(items) {
      const res = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({ items })
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        const err = new Error("Cart add failed");
        err.status = res.status;
        err.body = text;
        throw err;
      }
      return res.json();
    }

    function openCartDrawerOrRedirect() {
      const drawer = document.querySelector("cart-drawer");
      const bubble = document.querySelector("#cart-icon-bubble");
      if (drawer && typeof drawer.open === "function") {
        drawer.open(bubble || document.activeElement);
        return;
      }
      if (bubble) bubble.click();
      else window.location.href = "/cart";
    }

    function dispatchCartEvents() {
      document.dispatchEvent(new CustomEvent("cart:refresh"));
      document.dispatchEvent(new CustomEvent("cart:updated"));
      window.dispatchEvent(new CustomEvent("cart:refresh"));
      window.dispatchEvent(new CustomEvent("cart:updated"));
    }

    async function applyDiscountAsync(code) {
      try {
        await fetch(`/discount/${encodeURIComponent(code)}`);
      } catch (e) {
        /* ignore */
      }
    }

    function injectDiscountMessage(code) {
      if (!code) return;
      const drawer = document.getElementById("CartDrawer");
      if (!drawer) return;

      const footer = drawer.querySelector(".cart-drawer__footer");
      const oldMsg = drawer.querySelector(".offer-cta-discount-msg");
      if (oldMsg) oldMsg.remove();

      if (footer) {
        const msg = document.createElement("div");
        msg.className = "offer-cta-discount-msg";
        msg.style.cssText = "margin-bottom: 10px; text-align: center; color: #128038; font-weight: bold; background: #eafbea; padding: 8px; border-radius: 4px;";
        msg.innerHTML = `<small>Discount <strong>${code}</strong> applied at checkout!</small>`;
        footer.insertBefore(msg, footer.firstChild);
      }

      const checkoutBtn = drawer.querySelector("#CartDrawer-Checkout");
      if (checkoutBtn) {
        checkoutBtn.onclick = function(e) {
          e.preventDefault();
          window.location.href = `/checkout?discount=${encodeURIComponent(code)}`;
        };
      }
    }

    function monitorDrawerForDiscount(code) {
      injectDiscountMessage(code);

      const drawerInfo = document.querySelector("cart-drawer-items") || document.getElementById("CartDrawer");
      if (!drawerInfo) return;
      
      const obs = new MutationObserver(() => injectDiscountMessage(code));
      obs.observe(drawerInfo, { childList: true, subtree: true });

      setTimeout(() => obs.disconnect(), 30000);
    }

    async function performAddToCart(loadingBtn) {
      syncRevliftFormLines();
      syncPurchaseOptionFromRevlift();
      if (purchaseOption !== "subscribe") clearRevliftSellingPlanInputsForOneTime();

      if (atcBtn) {
        atcBtn.disabled = true;
        if (loadingBtn === atcBtn) atcBtn.classList.add("is-loading");
      }
      if (promoBtn) {
        promoBtn.disabled = true;
        if (loadingBtn === promoBtn) promoBtn.classList.add("is-loading");
      }

      const ids = selectedIds();
      const revliftPlanByVariantId = purchaseOption === "subscribe" ? revliftSellingPlanByVariantId() : new Map();
      const counts = new Map();
      ids.forEach(id => counts.set(id, (counts.get(id) || 0) + 1));

      const items = Array.from(counts.entries()).map(([id, qty]) => {
        const item = { id: Number(id), quantity: qty };
        const variantHandle = String(variantsById.get(String(id))?.handle || "").trim();
        const handleSellingPlanId = Number((variantHandle && sellingPlansByHandle[variantHandle]) || 0);
        const variantSellingPlanId = Number(variantsById.get(String(id))?.spId || 0);
        const itemSellingPlanId = useProductFlavorMode
          ? (handleSellingPlanId || variantSellingPlanId)
          : (variantSellingPlanId || sellingPlanId);
        const revliftSellingPlanId = revliftPlanByVariantId.get(String(id));
        if (purchaseOption === "subscribe" && (revliftSellingPlanId || itemSellingPlanId)) {
          item.selling_plan = revliftSellingPlanId || itemSellingPlanId;
        }
        return item;
      });

      try {
        const DISCOUNT_BY_QTY = { 1: "FD-SAVE-15", 2: "FD-SAVE-30", 3: "FD-SAVE-45" };
        const code = purchaseOption === "one_time" ? (DISCOUNT_BY_QTY[bagQty] || null) : null;

        if (code) applyDiscountAsync(code);

        await addItems(items);
        window.__pupfordCartDrawerNeedsRefreshUntil = Date.now() + 5000;
        dispatchCartEvents();
        openCartDrawerOrRedirect();

        if (code) monitorDrawerForDiscount(code);

      } catch (err) {
        console.error(err);
        alert("Sorry — we couldn’t add that to cart. Please try again.");
      } finally {
        setTimeout(() => {
          if (atcBtn) {
            atcBtn.disabled = false;
            atcBtn.classList.remove("is-loading");
          }
          if (promoBtn) {
            promoBtn.disabled = false;
            promoBtn.classList.remove("is-loading");
          }
        }, 300);
      }
    }

    form?.addEventListener("submit", async (e) => {
      e.preventDefault();
      if (!atcBtn || atcBtn.disabled) return;
      await performAddToCart(atcBtn);
    });

    promoBtn?.addEventListener("click", async (e) => {
      e.preventDefault();
      if (!promoBtn || promoBtn.disabled) return;
      await performAddToCart(promoBtn);
    });

    const onResize = () => fitQtyPerkText();
    window.addEventListener("resize", onResize);

    if (qtyCardsWrap && window.ResizeObserver) {
      const ro = new ResizeObserver(() => fitQtyPerkText());
      ro.observe(qtyCardsWrap);
      root.__offerCTAv2RO = ro;
    }

    // init
    renderQtyCardPerks();

    setPurchaseOption(defaultPurchaseOption);
    setQty(defaultQty);

    setTimeout(fitQtyPerkText, 50);
    hydrateSellingPlanAllocationsIfMissing();
  }

  document.addEventListener("DOMContentLoaded", () => {
    document.querySelectorAll("[data-offer-cta]").forEach(initOfferCTAv2);
  });

  document.addEventListener("shopify:section:load", (e) => {
    e.target.querySelectorAll?.("[data-offer-cta]")?.forEach(initOfferCTAv2);
  });
})();

